/**
 * Kiểu dữ liệu Tự động hoá v2 (demo FE). Cấu trúc JSON giữ để backend lưu
 * nguyên sau này: workflow = nodes + edges + position (như flowConfig cũ),
 * thêm 2 khối `source` (nguồn dữ liệu) và `mapping` (ánh xạ payload → CRM).
 */

// ── Nguồn dữ liệu (connector) ────────────────────────────────────────
export type SourceType = 'kiotviet' | 'gsheet' | 'webhook' | 'fmrp' | 'chatmql' | 'schedule'

export const SOURCE_LABELS: Record<SourceType, string> = {
  kiotviet: 'KiotViet',
  gsheet: 'Google Sheet',
  webhook: 'Webhook chung',
  fmrp: 'App bên thứ 3 (Shopee/TikTok qua FMRP)',
  chatmql: 'Sự kiện ChatMQL',
  schedule: 'Lịch định kỳ',
}

export interface Connector {
  id: string
  type: SourceType
  name: string
  status: 'connected' | 'pending' | 'error' | 'paused'
  /** Cấu hình theo loại. Bí mật (Client ID/Secret) chỉ trả về dạng đã che "••••". */
  config: Record<string, unknown>
  /** URL webhook riêng của nguồn — nền tảng ngoài gọi về đây. */
  webhookUrl?: string
  /** Nguồn webhook chung có khoá ký HMAC (xem bằng API /secret). */
  coKhoaKy?: boolean
  coBiMat?: boolean
  lastSyncAt?: string | null
  lastEventAt?: string | null
  lastError?: string | null
  createdAt: string
}

/** Sự kiện một nguồn có thể phát. */
export interface SourceEventDef {
  key: string
  label: string
  moTa: string
}

export const SOURCE_EVENTS: Record<SourceType, SourceEventDef[]> = {
  kiotviet: [
    { key: 'order.update', label: 'Đơn hàng thay đổi', moTa: 'Tạo mới / đổi trạng thái / hoàn thành đơn đặt hàng' },
    { key: 'invoice.update', label: 'Hoá đơn thay đổi', moTa: 'Hoá đơn bán lẻ tạo/sửa' },
    { key: 'customer.update', label: 'Khách hàng thay đổi', moTa: 'Khách mới hoặc cập nhật thông tin' },
    { key: 'product.update', label: 'Sản phẩm thay đổi', moTa: 'Hàng hoá tạo/sửa/giá' },
  ],
  gsheet: [
    { key: 'row.added', label: 'Có dòng mới', moTa: 'Poll theo chu kỳ, dòng chưa xử lý' },
    { key: 'row.updated', label: 'Dòng được sửa', moTa: 'So theo cột định danh' },
  ],
  webhook: [{ key: 'received', label: 'Nhận payload', moTa: 'POST tới URL webhook của nguồn' }],
  fmrp: [
    { key: 'invoice.created', label: 'Đơn sàn về FMRP', moTa: 'Bảng invoice FMRP ghi outbox' },
    { key: 'invoice.status', label: 'Đơn sàn đổi trạng thái', moTa: 'Giao thành công / huỷ / hoàn' },
  ],
  chatmql: [
    { key: 'message_received', label: 'Khách nhắn tin đến', moTa: 'Tin mới trong hội thoại' },
    { key: 'order_completed', label: 'Hoàn tất đơn hàng', moTa: 'Đơn lên từ ChatMQL' },
    { key: 'tag_added', label: 'Gắn nhãn khách', moTa: 'Nhân viên/AI gắn nhãn' },
    { key: 'no_reply_24h', label: 'Không trả lời 24h', moTa: 'Tin khách chưa được trả lời' },
    { key: 'birthday_detected', label: 'Sinh nhật khách', moTa: 'Theo ngày sinh CRM' },
  ],
  schedule: [{ key: 'cron', label: 'Theo lịch', moTa: 'Hằng ngày / tuần / cron' }],
}

// ── Ánh xạ ───────────────────────────────────────────────────────────
export type Transform = 'none' | 'trim' | 'upper' | 'phone_vn' | 'number' | 'date'
export const TRANSFORM_LABELS: Record<Transform, string> = {
  none: 'Giữ nguyên', trim: 'Cắt khoảng trắng', upper: 'IN HOA', phone_vn: 'Chuẩn SĐT (0xxxxxxxxx)', number: 'Số', date: 'Ngày (ISO)',
}
export interface MappingRule {
  from: string // đường dẫn trong payload nguồn, hỗ trợ [] cho mảng
  to: string   // trường nội bộ, vd customer.phone, order.items[].name
  transform: Transform
}
export interface Mapping {
  id: string
  source: SourceType
  name: string
  rules: MappingRule[]
}

// ── Workflow ─────────────────────────────────────────────────────────
export type NodeKind = 'source' | 'mapping' | 'condition' | 'delay' | 'action'

export const ACTIONS_V2 = [
  'add_tag', 'remove_tag', 'send_zalo_personal', 'send_zalo_oa', 'send_message', 'send_zalo_zns', 'send_survey', 'create_voucher',
  'create_task', 'update_property', 'write_sheet', 'http_request', 'send_notification',
  'send_friend_request',
] as const
export type ActionV2 = (typeof ACTIONS_V2)[number]
export const ACTION_V2_LABELS: Record<ActionV2, string> = {
  add_tag: 'Gắn nhãn khách',
  remove_tag: 'Gỡ nhãn khách',
  send_zalo_personal: 'Gửi tin Zalo cá nhân',
  send_zalo_oa: 'Gửi tin Zalo OA',
  send_message: 'Gửi tin kênh khác (Facebook…)',
  send_zalo_zns: 'Gửi ZNS theo mẫu',
  send_survey: 'Gửi form khảo sát',
  create_voucher: 'Tạo voucher',
  create_task: 'Tạo việc cho nhân viên',
  update_property: 'Cập nhật thuộc tính CDP',
  write_sheet: 'Ghi Google Sheet',
  http_request: 'Gọi HTTP API',
  send_notification: 'Thông báo nội bộ',
  send_friend_request: 'Gửi lời mời kết bạn Zalo',
}

export const CONDITION_OPS_V2 = ['eq', 'neq', 'contains', 'not_contains', 'in', 'gte', 'lte', 'exists', 'not_exists'] as const
export type ConditionOpV2 = (typeof CONDITION_OPS_V2)[number]
export const CONDITION_OP_V2_LABELS: Record<ConditionOpV2, string> = {
  eq: 'Bằng', neq: 'Khác', contains: 'Chứa', not_contains: 'Không chứa', in: 'Thuộc danh sách (phẩy)',
  gte: '≥', lte: '≤', exists: 'Có giá trị', not_exists: 'Rỗng',
}
export interface ConditionRuleV2 {
  field: string
  op: ConditionOpV2
  value: string
}

export interface FlowNodeV2 {
  id: string
  kind: NodeKind
  label: string
  position: { x: number; y: number }
  /** source: { connectorId, event } · mapping: { mappingId } · condition: { logic: 'and'|'or', rules } · delay: { value, unit } · action: { type, ...} */
  config: Record<string, unknown>
}
export interface FlowEdgeV2 {
  source: string
  target: string
  /** 'true' | 'false' cho nhánh điều kiện. */
  label?: 'true' | 'false'
}
export interface WorkflowV2 {
  id: string
  name: string
  description?: string
  enabled: boolean
  nodes: FlowNodeV2[]
  edges: FlowEdgeV2[]
  createdAt: string
  updatedAt: string
  lastRunAt?: string | null
  lastRunStatus?: 'ok' | 'fail' | 'waiting' | 'running' | 'skipped' | null
  /** Id mẫu gốc (wf_cham_soc_30_ngay…) khi luồng được nạp từ thư viện mẫu. */
  templateKey?: string | null
}

// ── Sự kiện mẫu & lần chạy ───────────────────────────────────────────
export interface SampleEvent {
  id: string
  source: SourceType
  event: string
  label: string
  /** Payload nguyên bản (đúng dạng nguồn gửi). */
  payload: unknown
}

export interface RunStep {
  nodeId: string
  kind: NodeKind
  label: string
  status: 'ok' | 'skip' | 'fail' | 'wait'
  /** Tóm tắt một dòng cho UI. */
  summary: string
  input?: unknown
  output?: unknown
  /** Nhánh đã đi (điều kiện). */
  branch?: 'true' | 'false'
}
export interface WorkflowRun {
  id: string
  workflowId: string
  workflowName: string
  sampleId: string
  sampleLabel: string
  startedAt: string
  /** Tổng thời gian luồng kéo dài (cộng các khối Chờ) — chạy thử thì tua nhanh. */
  durationMs?: number
  /** running/waiting: lượt thật đang chạy hoặc đang ở khối Chờ. */
  status: 'ok' | 'fail' | 'running' | 'waiting' | 'skipped'
  /** live = chạy thật trên máy chủ · test = chạy thử trên trình duyệt */
  mode?: 'live' | 'test'
  resumeAt?: string | null
  waitKind?: string | null
  error?: string | null
  steps: RunStep[]
  /** Ngữ cảnh cuối (record đã ánh xạ + biến do hành động sinh). */
  context: Record<string, unknown>
}

export const genId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 9)}`
